import React, { FC } from "react";
import "./index.scss";
import "./v2.scss";
import { omitNation } from "../../helpers";

type EventType = "live" | "view";

export interface IEvent {
  title: string;
  linkUrl: string;
  linkTarget?: string;
  type: EventType;
  location: string;
  nationalityCode: string;
  dates: string;
}

interface Props {
  title?: string;
  linkText?: string;
  linkUrl?: string;
  linkTarget?: string;
  text?: string;
  list: IEvent[];
}

const EVENT_ACTION: Record<EventType, Record<string, unknown>> = {
  live: {
    icon: (
      <svg
        className="icon"
        width="23"
        height="21"
        viewBox="0 0 23 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20.5 4.2793H12.91L16.2 0.989297L15.5 0.279297L11.5 4.2793L7.5 0.279297L6.79 0.989297L10.09 4.2793H2.5C1.4 4.2793 0.5 5.1693 0.5 6.2793V18.2793C0.5 19.3793 1.4 20.2793 2.5 20.2793H20.5C21.6 20.2793 22.5 19.3793 22.5 18.2793V6.2793C22.5 5.1693 21.6 4.2793 20.5 4.2793ZM20.5 18.2793H2.5V6.2793H20.5V18.2793ZM8.5 8.2793V16.2793L15.5 12.2793L8.5 8.2793Z"
          fill="currentColor"
        />
      </svg>
    ),
    title: "Watch Live"
  },
  view: {
    icon: (
      <svg
        className="icon"
        width="23"
        height="16"
        viewBox="0 0 23 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.5 0.779297C6.5 0.779297 2.23 3.8893 0.5 8.2793C2.23 12.6693 6.5 15.7793 11.5 15.7793C16.5 15.7793 20.77 12.6693 22.5 8.2793C20.77 3.8893 16.5 0.779297 11.5 0.779297ZM11.5 13.2793C8.74 13.2793 6.5 11.0393 6.5 8.2793C6.5 5.5193 8.74 3.2793 11.5 3.2793C14.26 3.2793 16.5 5.5193 16.5 8.2793C16.5 11.0393 14.26 13.2793 11.5 13.2793ZM11.5 5.2793C9.84 5.2793 8.5 6.6193 8.5 8.2793C8.5 9.9393 9.84 11.2793 11.5 11.2793C13.16 11.2793 14.5 9.9393 14.5 8.2793C14.5 6.6193 13.16 5.2793 11.5 5.2793Z"
          fill="currentColor"
        />
      </svg>
    ),
    title: "view"
  }
};

const EventIcon = ({ name }: { name: string }) => {
  return <>{EVENT_ACTION[name].icon}</>;
};

const UpcomingEvents: FC<Props> = ({
  list,
  title,
  text,
  linkTarget = "_self",
  linkText,
  linkUrl
}) => {
  return (
    <div className="upcoming-events">
      {title && <div className="upcoming-events__title">{title}</div>}
      <div className="upcoming-events__list">
        {list.map(
          (
            {
              title,
              type,
              location,
              nationalityCode,
              dates,
              linkTarget,
              linkUrl
            },
            key
          ) => (
            <div key={key} className={`upcoming-events__block ${type}`}>
              <div className="upcoming-events__block--title">
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 0.279297C4.48998 0.279297 0 4.74481 0 10.2793C0 15.8138 4.47622 20.2793 10 20.2793C15.5238 20.2793 20 15.797 20 10.2793C20 4.76163 15.5146 0.279297 10 0.279297ZM9.46934 9.75016H5.41673C5.48555 8.16888 5.87093 6.63042 6.5377 5.22348H9.46934V9.75016ZM9.46934 15.3351H6.53617C5.87093 13.9282 5.48555 12.3912 5.41673 10.8084H9.46934V15.3351ZM9.46934 19.2868C8.53036 18.4702 7.73207 17.4364 7.10353 16.3934H9.46934V19.2868ZM7.10506 4.16521C7.7336 3.12071 8.53189 2.08844 9.47087 1.2718V4.16521H7.10506ZM7.77489 1.54249C7.03777 2.33925 6.40924 3.22164 5.90152 4.16521H3.37666C4.54045 2.90355 6.05903 1.9768 7.77489 1.54249ZM2.53556 5.22348H5.39073C4.78208 6.64419 4.43034 8.17806 4.36917 9.75016H1.00321C1.09803 8.07866 1.65163 6.52949 2.53709 5.22348M4.36917 10.8084C4.43187 12.3805 4.78361 13.9144 5.39073 15.3351H2.53556C1.6501 14.0291 1.0965 12.4799 1.00168 10.8084H4.36764H4.36917ZM3.37666 16.3934H5.89999C6.40924 17.337 7.03472 18.2193 7.77336 19.0161C6.06056 18.5787 4.54045 17.652 3.37666 16.3934ZM10.5307 19.2776V1.28098C12.6181 1.40179 14.5145 2.2322 15.981 3.53362C14.5756 4.9375 13.3904 7.58317 13.3904 10.2778C13.3904 12.9724 14.597 15.641 15.981 17.0219C14.5145 18.3233 12.6166 19.1537 10.5307 19.2746M17.4644 15.3351C17.2396 15.6639 16.998 15.9759 16.735 16.271C15.2516 14.6056 14.4533 12.4754 14.4533 10.2793C14.4533 8.08324 15.2546 5.94989 16.735 4.28756C18.2459 5.98353 19.0136 8.12147 19.0136 10.2793C19.0136 12.038 18.5028 13.8028 17.4644 15.3351Z"
                    fill="currentColor"
                  />
                </svg>
                <div>{title}</div>
              </div>
              <div className="upcoming-events__block--info">
                <span
                  className={`itf-flags itf-flags--${omitNation(
                    nationalityCode
                  )}`}
                />
                <div>
                  <div className="upcoming-events__block--info-location">
                    {location}
                  </div>
                  <div className="upcoming-events__block--info-date">
                    {dates}
                  </div>
                </div>
              </div>
              <a
                className="upcoming-events__block--action"
                target={linkTarget || "_self"}
                href={linkUrl}
              >
                <div>
                  <EventIcon name={type} />
                  {EVENT_ACTION[type].title}
                </div>
                <svg
                  width="8"
                  height="13"
                  viewBox="0 0 8 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.589966 10.8693L5.16997 6.2793L0.589966 1.6893L1.99997 0.279297L7.99997 6.2793L1.99997 12.2793L0.589966 10.8693Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            </div>
          )
        )}
      </div>
      {linkText && linkUrl && (
        <a href={linkUrl} target={linkTarget} className="upcoming-events__btn">
          {linkText}{" "}
          <svg
            width="7"
            height="11"
            viewBox="0 0 7 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 9.1043L3.81667 5.2793L0 1.4543L1.175 0.279297L6.175 5.2793L1.175 10.2793L0 9.1043Z"
              fill="currentColor"
            />
          </svg>
        </a>
      )}
      {text && <div className="upcoming-events__text">{text}</div>}
    </div>
  );
};

export default UpcomingEvents;
